import { Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Investor } from "../../../../../api/types/fundStructureTypes";
import type { LookupObjectReference } from "../../../../../api/types/objectTypes";
import MultiselectAutocomplete from "../../../../common/MultiselectAutocomplete";
import InvestorCreateDialog from "../../investors/InvestorCreateDialog";

interface Props {
  open: boolean;
  title: string;
  description: string;
  excludeInvestorIds: string[];
  onClose: () => void;
  onSelected: (items: Investor[]) => void;
}

const searchInvestors = withErrorHandling(adminApi.searchInvestors);

const AssignInvestorsDialog = ({ open, title, description, excludeInvestorIds, onClose, onSelected }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const fetchInvestorReferences = useCallback(() => adminApi.getObjectsAsLookupReferences("Investor"), []);
  const [investorReferences, fetchError] = useFetch(fetchInvestorReferences, { disabled: !open });

  const [selectedReferences, setSelectedReferences] = useState<LookupObjectReference[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const references = investorReferences ?? [];
  const options = references.filter((reference) => !excludeInvestorIds.includes(reference.objectId));

  const handleClose = () => {
    setSelectedReferences([]);
    onClose();
  };

  const handleCreateNew = () => {
    handleClose();
    setShowCreateDialog(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const investorIds = selectedReferences.map((reference) => reference.objectId);
    const [response, error] = await searchInvestors({
      fieldIds: [],
      includeContacts: false,
      includeFunds: true,
      investorIds,
    });
    setIsSubmitting(false);

    if (error) {
      logError(error, "[AssignInvestorsDialog] searchInvestors");
      sendNotificationError(error.message);
      return;
    }

    const investorsWithFunds = response.items.filter((investor) => investor.funds.length > 0);
    const skippedNames = selectedReferences
      .filter((reference) => !investorsWithFunds.some((investor) => investor.id === reference.objectId))
      .map((reference) => reference.primaryFieldValue);

    if (skippedNames.length > 0) {
      sendNotificationError(`${skippedNames.join(", ")} cannot be added without associated funds.`);
    }

    if (investorsWithFunds.length === 0) {
      return;
    }

    onSelected(investorsWithFunds);
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogCloseButton onClick={handleClose} />

        <DialogContent>
          {fetchError && (
            <Typography variant="subtitle2" color="error">
              Failed to load investors
            </Typography>
          )}
          {!fetchError && investorReferences === undefined && <InlineLoader />}
          {!fetchError && investorReferences !== undefined && (
            <Stack spacing={2}>
              <Typography color="textSecondary">{description}</Typography>
              <MultiselectAutocomplete
                dense
                options={options}
                values={selectedReferences}
                onSelected={setSelectedReferences}
                getOptionLabel={(option) => option.primaryFieldValue}
                getOptionValue={(option) => option.objectId}
                noItemsText="No investors"
                onCreateOption={handleCreateNew}
              />
            </Stack>
          )}
        </DialogContent>

        <DialogButtons onCancel={handleClose}>
          <Button
            loading={isSubmitting}
            disabled={selectedReferences.length === 0}
            onClick={handleSubmit}
            variant="contained"
          >
            Add Investor(s)
          </Button>
        </DialogButtons>
      </Dialog>
      <InvestorCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        existingInvestorNames={references.map((reference) => reference.primaryFieldValue)}
        onCreated={() => setShowCreateDialog(false)}
      />
    </>
  );
};

export default AssignInvestorsDialog;
